import type { LucideIcon } from 'lucide-react'
import clsx from 'clsx'
import { Button } from './Button'

interface EmptyStateProps {
  icon: LucideIcon
  title: string
  description: string
  action?: {
    label: string
    onClick?: () => void
    href?: string
  }
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

/**
 * Empty state placeholder for pages with no data yet.
 * MUST provide a clear action when data can be created.
 * Never shows fake/demo data.
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  size = 'md',
  className,
}: EmptyStateProps) {
  const sizeConfig = {
    sm: { icon: 32, wrapper: 'py-8', iconWrapper: 'w-12 h-12' },
    md: { icon: 40, wrapper: 'py-12', iconWrapper: 'w-16 h-16' },
    lg: { icon: 48, wrapper: 'py-16', iconWrapper: 'w-20 h-20' },
  }
  const cfg = sizeConfig[size]

  return (
    <div
      className={clsx(
        'flex flex-col items-center justify-center text-center',
        'rounded-xl border border-dashed border-slate-700/70',
        'bg-slate-800/20',
        cfg.wrapper,
        className
      )}
    >
      <div
        className={clsx(
          'flex items-center justify-center rounded-xl mb-4',
          'bg-slate-800/60 border border-slate-700/50',
          cfg.iconWrapper
        )}
      >
        <Icon size={cfg.icon} className="text-slate-500" />
      </div>

      <h3 className="text-sm font-semibold text-slate-300 mb-1.5">{title}</h3>
      <p className="text-xs text-slate-500 max-w-xs leading-relaxed mb-4">{description}</p>

      {action && (
        <Button
          variant="outline"
          size="sm"
          onClick={action.onClick}
        >
          {action.label}
        </Button>
      )}
    </div>
  )
}
